import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listInvoicesBetween } from "../../api/feeApi";
import { listCashEquivalentLedgers } from "../../api/accountApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `fee-report-date-to-date-head-collection`
 * ("Fee Collect Summary") — one row per fee head: total real fee collected
 * vs. fine collected vs. combined total, for a date range and optional
 * ledger filter. Confirmed via the old app's own real native-SQL query
 * (`FeeInvoiceDetailsRepository.findDateToDateFeesCollectionByLedgerWiise`,
 * backing `/student-fee/report/date-to-date/fee-head/collection`):
 *   feeCollection  = sum(fee_paid) + sum(due_paid)
 *   fineCollection = sum(fine_paid)
 *   totalCollection = feeCollection + fineCollection
 * i.e. "Fee Collection" also folds in due-amount payments, not just the
 * fee-structure amount — built entirely client-side from the already-exposed
 * `GET /api/fee-invoices/range` (each line already carries
 * `feePaid`/`duePaid`/`finePaid` and the invoice its `ledgerId`) — no new
 * backend needed.
 */
export default function FeeCollectSummaryPage() {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [ledgers, setLedgers] = useState([]);
  const [ledgerIds, setLedgerIds] = useState([]);
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Real old-app endpoint (`/general/accounts/report/cash-equivalance/dfps/ledger/list`)
    // is category_default_id {102,103} — the exact same set as `listCashEquivalentLedgers`
    // already built for this migration's Auto Ledger Setup/Cash Book pickers — found while
    // sweeping Fee Management (previously used the unfiltered `listLedgers`; see MIGRATION.md).
    listCashEquivalentLedgers().then(setLedgers);
  }, []);

  const toggleLedger = (id) => setLedgerIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      const data = await listInvoicesBetween(fromDate, toDate);
      setInvoices(data);
    } catch (err) {
      setError("Failed to load collection summary.");
    } finally {
      setLoading(false);
    }
  };

  const rows = useMemo(() => {
    if (!invoices) return [];
    const filtered = ledgerIds.length === 0 ? invoices : invoices.filter((inv) => ledgerIds.includes(String(inv.ledgerId)));
    const byHead = new Map();
    filtered.forEach((inv) => {
      (inv.lines || []).forEach((line) => {
        const key = line.feeHeadName;
        if (!byHead.has(key)) byHead.set(key, { feeHeadName: key, feeCollection: 0, fineCollection: 0 });
        const row = byHead.get(key);
        row.feeCollection += (line.feePaid || 0) + (line.duePaid || 0);
        row.fineCollection += line.finePaid || 0;
      });
    });
    return Array.from(byHead.values()).sort((a, b) => a.feeHeadName.localeCompare(b.feeHeadName));
  }, [invoices, ledgerIds]);

  const totalFee = rows.reduce((sum, r) => sum + r.feeCollection, 0);
  const totalFine = rows.reduce((sum, r) => sum + r.fineCollection, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Collect Summary</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {ledgers.length > 0 && (
        <div className="form-row">
          <span className="admit-card-label">Filter by Ledger:</span>{" "}
          {ledgers.map((l) => (
            <label key={l.ledgerId} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
              <input type="checkbox" checked={ledgerIds.includes(String(l.ledgerId))} onChange={() => toggleLedger(String(l.ledgerId))} />
              {l.ledgerName}
            </label>
          ))}
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        invoices && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Fee Head</th>
                  <th>Fee Collection</th>
                  <th>Fine Collection</th>
                  <th>Total Collection</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.feeHeadName}>
                    <td>{r.feeHeadName}</td>
                    <td>{Math.round(r.feeCollection)}</td>
                    <td>{Math.round(r.fineCollection)}</td>
                    <td>{Math.round(r.feeCollection + r.fineCollection)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4}>No collection found for this selection.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <strong>Grand Total</strong>
                  </td>
                  <td>
                    <strong>{Math.round(totalFee)}</strong>
                  </td>
                  <td>
                    <strong>{Math.round(totalFine)}</strong>
                  </td>
                  <td>
                    <strong>{Math.round(totalFee + totalFine)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
